import { Color } from 'three';

import glslConst from '@resn/gozer-glsl/math/const.glsl';
import glslCircle from '@resn/gozer-glsl/shapes/circle.glsl';
import glslDraw from '@resn/gozer-glsl/shapes/draw.glsl';
import glslPoly from '@resn/gozer-glsl/shapes/poly.glsl';
import glslRect from '@resn/gozer-glsl/shapes/rect.glsl';
import { randomFloat } from '@resn/gozer-math';
import { CustomMaterial } from '@resn/gozer-three/materials';

import {
    shaderChunks as radialPulseChunks,
    uniforms as radialPulseUniforms,
} from '@/components/gl/shaders/ShaderChunks/radial-pulse-gradient';

export const createPatternBg = ({
    wireframe = false,
    alpha = 1.0,
    color = '#ff0000',
    aspect = 1,
    repeat = 40,
    shapeScale = 0.8,
    warpStrength = 0.1,
    warpFreq = 0.35,
    warpSpeed = 0.4,
    maskAngle = 0.8,
    maskEdge = 0.6,
    vertexDistortion = false,
    transparent = true,
    useDepthScale = false,
    depthScaleStartThreshold = 0.8,
    useTexture = false,
    texture = null,
    colorProgress = 0,
    pulseAlpha = 5.0,
} = {}) => {
    const timeOffset = randomFloat(0, 1000);

    const material = new CustomMaterial({
        options: {
            wireframe,
            transparent,
            depthWrite: false,
            depthTest: true,
        },
        uniforms: {
            uTime: 0,
            uTimeOffset: timeOffset,
            uColor: new Color(color),
            uAlpha: alpha,
            uAspect: aspect,
            uRepeat: repeat,
            uPattern: 0,
            uShapeScale: shapeScale,
            uWarpStrength: warpStrength,
            uWarpFreq: warpFreq,
            uWarpSpeed: warpSpeed,
            uMaskAngle: maskAngle,
            uMaskEdge: maskEdge,
            uIsWireframe: wireframe,
            uIsVertexDistortion: vertexDistortion,
            uUseDepthScale: useDepthScale,
            uDepthScaleStartThreshold: depthScaleStartThreshold,
            uUseTexture: useTexture,
            uTexture: texture,
            uColorProgress: colorProgress,
            uPulseAlpha: pulseAlpha,

            ...radialPulseUniforms(),
        },
        vs: /* glsl */ `
            uniform float uAspect;
            uniform float uTime;
            uniform float uTimeOffset;
            uniform float uWarpStrength;
            uniform float uWarpFreq;
            uniform float uWarpSpeed;
            uniform bool uIsVertexDistortion;
            varying vec2 vUv;
            varying vec4 vClipPosition;

            ${glslConst}

            vec2 warpUvVs(vec2 uv, float t) {
                float str = uWarpStrength;
                float freq = uWarpFreq;

                float dx = sin(uv.y * freq * PI * 2.0 + t * 1.3) * str
                        + sin(uv.y * freq * PI * 4.7 - t * 0.7) * str * 0.5
                        + cos(uv.x * freq * PI * 3.1 + t * 0.9) * str * 0.3;

                float dy = cos(uv.x * freq * PI * 2.0 - t * 1.1) * str
                        + cos(uv.x * freq * PI * 5.3 + t * 0.6) * str * 0.5
                        + sin(uv.y * freq * PI * 2.7 - t * 0.8) * str * 0.3;

                return uv + vec2(dx, dy);
            }

            void main() {
                vUv = uv;
                vec3 pos = position;

                if (uIsVertexDistortion) {
                    vec2 p = position.xy;
                    vec2 st = vec2((p.x + 0.5), p.y + 0.5);
                    float t = uTime * uWarpSpeed + uTimeOffset;
                    vec2 dSt = warpUvVs(st, t) - st;
                    pos.z += dSt.x + dSt.y;
                }

                gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);

                vClipPosition = gl_Position;
            }`,
        fs: /* glsl */ `
            uniform vec3 uColor;
            uniform float uAlpha;
            uniform float uColorProgress;
            uniform float uAspect;
            uniform float uRepeat;
            uniform int uPattern;
            uniform float uShapeScale;
            uniform float uTime;
            uniform float uTimeOffset;
            uniform float uWarpStrength;
            uniform float uWarpFreq;
            uniform float uWarpSpeed;
            uniform float uMaskAngle;
            uniform float uMaskEdge;
            uniform bool uIsWireframe;
            uniform bool uIsVertexDistortion;
            uniform bool uUseDepthScale;
            uniform bool uUseTexture;
            uniform sampler2D uTexture;
            uniform float uDepthScaleStartThreshold;
            uniform float uPulseAlpha;
            
            varying vec2 vUv;
            varying vec4 vClipPosition;
            
            ${radialPulseChunks.uniforms}

            ${glslConst}
            ${glslDraw}
            ${glslCircle}
            ${glslRect}
            ${glslPoly}


            ${radialPulseChunks.functions}

            vec2 warpUv(vec2 uv, float t) {
                float str = uWarpStrength;
                float freq = uWarpFreq;

                float dx = sin(uv.y * freq * PI * 2.0 + t * 1.3) * str
                        + sin(uv.y * freq * PI * 4.7 - t * 0.7) * str * 0.5
                        + cos(uv.x * freq * PI * 3.1 + t * 0.9) * str * 0.3;

                float dy = cos(uv.x * freq * PI * 2.0 - t * 1.1) * str
                        + cos(uv.x * freq * PI * 5.3 + t * 0.6) * str * 0.5
                        + sin(uv.y * freq * PI * 2.7 - t * 0.8) * str * 0.3;

                return uv + vec2(dx, dy);
            }

            float shapeMask(vec2 uv) {
                vec2 c = uv - 0.5;
                // project onto the axis perpendicular to the band direction
                vec2 dir = vec2(cos(uMaskAngle), sin(uMaskAngle));
                float d = abs(dot(c, dir));
                // 0 at center band, 1 at edges — remap so the transition is controllable
                return smoothstep(0.0, uMaskEdge, d);
            }

            void main() {
                float rep = max(uRepeat, 0.0001);
                if (uUseTexture) {
                    rep = 12.0;
                }

                float t = uTime * uWarpSpeed + uTimeOffset;

                vec2 st = vec2(vUv.x, vUv.y);
                if (!uIsVertexDistortion) {
                    st = warpUv(st, t);
                }

                vec2 tiling = st * rep;
                vec2 cellUv = fract(tiling);

                // scale driven by distance from the diagonal band
                float mask = shapeMask(vUv);
                float depthScale = uUseDepthScale ? smoothstep(0.0, uDepthScaleStartThreshold, vUv.x) : 1.0;
                
                float depthMaskScale = mask * depthScale;
                float sc = uShapeScale * depthMaskScale;
                float depthMaskAlpha = mix(0.5, 1.0, smoothstep(0.4, 1.0, depthMaskScale));
                
                float pattern = 0.0;
                if (uUseTexture) {
                    // Match procedural branches: scale each cell’s sample from center (same sc as circle/rect).
                    // Plane still tiles via fract(tiling); within one cell, only one 0..1 tile (no repeat when zoomed out).
                    float scSafe = max(sc, 1e-4);
                    
                    scSafe = 1.0;
                    depthMaskAlpha = mix(0.0, 1.0, smoothstep(0.0, 1.0, depthMaskScale));

                    vec2 texUv = (cellUv - 0.5) / scSafe + 0.5;
                    float inCell = step(0.0, texUv.x) * step(texUv.x, 1.0) * step(0.0, texUv.y) * step(texUv.y, 1.0);
                    pattern = texture2D(uTexture, clamp(texUv, 0.0, 1.0)).r * inCell;
                    
                } else if (uPattern == 0) {
                    pattern = circle(cellUv, sc);
                } else if (uPattern == 1) {
                    pattern = rect(cellUv, sc);
                } else if (uPattern == 2) {
                    pattern = poly(cellUv - 0.5, 0.6 * sc * mask, 3);
                } else {
                    vec2 p = cellUv - 0.5;
                    float k = 0.7071067811865476;
                    vec2 r = vec2(p.x * k + p.y * k, -p.x * k + p.y * k);
                    pattern = rect(r + 0.5, 0.7 * sc * mask);
                }

                float uuAlpha = uAlpha * (uUseTexture ? 1.0 : 1.0);
                float alpha = pattern * uuAlpha * depthMaskAlpha;

                vec3 color = mix(vec3(1.0), uColor, uColorProgress);

                // Pulse effect — brighten dots toward white (matches border glow, avoids vec4 += blowout)
                vec2 ndc = vClipPosition.xy / max(vClipPosition.w, 1e-5);
                vec2 screenUv = ndc * 0.5 + 0.5;
                float pulseMask = radialPulseGradient(
                    screenUv,
                    uPulseProgress,
                    uPulseThickness,
                    uPulseFalloff,
                    uPulseMaxReach
                );
                float pulseStrength =
                    pulseMask * smoothstep(0.0, 0.1, uPulseProgress) * uPulseAlpha * pattern;
                vec3 finalColor = mix(color, vec3(1.0), pulseStrength);
                float finalAlpha = clamp(alpha + pulseStrength * (1.0 - alpha), 0.0, 1.0);

                gl_FragColor = vec4(finalColor, finalAlpha);

                if (uIsWireframe) {
                    gl_FragColor = vec4(1.0, 0.0, 0.0, 1.0);
                }
                
                // gl_FragColor = vec4(1.0, 0.0, 0.0, pattern);
            }`,
    });

    return material;
};
